import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Save, Cable } from '@mui/icons-material';
import { dhis2ConfigAPI, type Dhis2ConfigOut, type Dhis2ConnectionOut } from '../../services/api';

interface ConnectionSchemaProps {
  configs: Dhis2ConfigOut[];
  selectedConfigId: string | null;
  onSelectConfigId: (id: string | null) => void;
  /** Called after config is updated (e.g. review_before_push); parent may refetch configs */
  onConfigUpdated?: () => void;
}

export default function ConnectionSchema({ configs, selectedConfigId, onSelectConfigId, onConfigUpdated }: ConnectionSchemaProps) {
  const [connection, setConnection] = useState<Dhis2ConnectionOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string; error?: string; version?: string } | null>(null);

  // Form state (mirrors connection + unsaved token)
  const [baseUrl, setBaseUrl] = useState('');
  const [authType, setAuthType] = useState('api_token');
  const [apiToken, setApiToken] = useState('');
  const [metadataEndpoint, setMetadataEndpoint] = useState('');
  const [programSpecificUrlTemplate, setProgramSpecificUrlTemplate] = useState('');
  const [programIds, setProgramIds] = useState<string[]>([]);
  const [cacheTtlSeconds, setCacheTtlSeconds] = useState<number | ''>(3600);
  const [timeoutSeconds, setTimeoutSeconds] = useState<number | ''>(30);
  const [programdataApiUrl, setProgramdataApiUrl] = useState('');
  const [programdataHttpMethod, setProgramdataHttpMethod] = useState<'GET' | 'POST'>('GET');
  const [programdataAuthSameAsDhis2, setProgramdataAuthSameAsDhis2] = useState(true);
  const [username, setUsername] = useState(''); // stored: for Basic auth (password = API token)
  const [systemInfoPath, setSystemInfoPath] = useState(''); // e.g. /service/api/system/info when Test returns 404
  const [savingReviewOption, setSavingReviewOption] = useState(false);
  const [testUsername, setTestUsername] = useState(''); // DHIS2 username for Test connection only (when testing before save)

  const loadConnection = async () => {
    if (!selectedConfigId) {
      setConnection(null);
      setBaseUrl('');
      setAuthType('api_token');
      setApiToken('');
      setMetadataEndpoint('');
      setProgramSpecificUrlTemplate('');
      setProgramIds([]);
      setCacheTtlSeconds(3600);
      setTimeoutSeconds(30);
      setProgramdataApiUrl('');
      setProgramdataHttpMethod('GET');
      setProgramdataAuthSameAsDhis2(true);
      setUsername('');
      setSystemInfoPath('');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await dhis2ConfigAPI.getConnection(selectedConfigId);
      const conn = res.data ?? null;
      setConnection(conn);
      if (conn) {
        setBaseUrl(conn.base_url ?? '');
        setAuthType(conn.auth_type ?? 'api_token');
        setApiToken(''); // Never show stored token
        setMetadataEndpoint(conn.metadata_endpoint ?? '');
        setProgramSpecificUrlTemplate(conn.program_specific_url_template ?? '');
        const ids = conn.program_ids ?? (conn.program_id_filter ? [conn.program_id_filter] : []);
        setProgramIds(Array.isArray(ids) ? ids.filter((s) => s != null && String(s).trim() !== '') : []);
        setCacheTtlSeconds(conn.cache_ttl_seconds ?? 3600);
        setTimeoutSeconds(conn.timeout_seconds ?? 30);
        setProgramdataApiUrl(conn.programdata_api_url ?? '');
        setProgramdataHttpMethod((conn.programdata_http_method as 'GET' | 'POST') ?? 'GET');
        setProgramdataAuthSameAsDhis2(conn.programdata_auth_same_as_dhis2 ?? true);
        setUsername(conn.username ?? '');
        setSystemInfoPath(conn.system_info_path ?? '');
      } else {
        setBaseUrl('');
        setAuthType('api_token');
        setApiToken('');
        setMetadataEndpoint('');
        setProgramSpecificUrlTemplate('');
        setProgramIds([]);
        setCacheTtlSeconds(3600);
        setTimeoutSeconds(30);
        setProgramdataApiUrl('');
        setProgramdataHttpMethod('GET');
        setProgramdataAuthSameAsDhis2(true);
        setUsername('');
        setSystemInfoPath('');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load connection');
      setConnection(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnection();
  }, [selectedConfigId]);

  const handleSave = async () => {
    if (!selectedConfigId) return;
    setSaving(true);
    setError(null);
    setTestResult(null);
    try {
      await dhis2ConfigAPI.setConnection(selectedConfigId, {
        base_url: baseUrl?.trim() || null,
        auth_type: authType,
        ...(apiToken !== undefined && apiToken !== '' ? { api_token_encrypted: apiToken } : {}),
        username: username?.trim() || null,
        system_info_path: systemInfoPath?.trim() || null,
        metadata_endpoint: metadataEndpoint?.trim() || null,
        program_specific_url_template: programSpecificUrlTemplate?.trim() || null,
        program_ids: programIds.length ? programIds.map((s) => s.trim()).filter(Boolean) : null,
        cache_ttl_seconds: cacheTtlSeconds === '' ? null : Number(cacheTtlSeconds),
        timeout_seconds: timeoutSeconds === '' ? null : Number(timeoutSeconds),
        programdata_api_url: programdataApiUrl?.trim() || null,
        programdata_http_method: programdataHttpMethod,
        programdata_auth_same_as_dhis2: programdataAuthSameAsDhis2,
      });
      await loadConnection();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedConfigId) return;
    if (!baseUrl?.trim()) {
      setTestResult({ success: false, message: 'Enter DHIS2 base URL first.' });
      return;
    }
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const res = await dhis2ConfigAPI.testConnection(selectedConfigId, {
        base_url: baseUrl.trim(),
        api_token: apiToken?.trim() || undefined,
        username: (authType === 'basic' ? username : testUsername)?.trim() || undefined,
        timeout_seconds: timeoutSeconds === '' ? undefined : Number(timeoutSeconds) || 30,
        system_info_path: systemInfoPath?.trim() || undefined,
      });
      const data = res.data ?? {};
      setTestResult(data);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string; message?: string } } };
      const msg = err?.response?.data?.detail ?? err?.response?.data?.message ?? (e instanceof Error ? e.message : 'Test failed');
      setTestResult({ success: false, message: String(msg) });
    } finally {
      setTesting(false);
    }
  };

  const selectedConfig = configs.find((c) => c.id === selectedConfigId);
  const handleReviewBeforePushChange = async (checked: boolean) => {
    if (!selectedConfigId) return;
    setSavingReviewOption(true);
    setError(null);
    try {
      await dhis2ConfigAPI.updateConfig(selectedConfigId, { review_before_push: checked });
      onConfigUpdated?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to update option');
    } finally {
      setSavingReviewOption(false);
    }
  };

  const showDhis2Section =
    selectedConfig &&
    (selectedConfig.schema_source === 'dhis2_metadata' || selectedConfig.schema_source === 'dhis2_program_data_json');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Config</InputLabel>
          <Select
            value={selectedConfigId ?? ''}
            label="Config"
            onChange={(e) => onSelectConfigId(e.target.value || null)}
          >
            <MenuItem value="">— Select config —</MenuItem>
            {configs.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name} ({c.schema_source})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedConfig && (
          <Typography variant="body2" color="text.secondary">
            Schema source: {selectedConfig.schema_source}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'warning'}
          onClose={() => setTestResult(null)}
          sx={{ mb: 2 }}
        >
          {testResult.success
            ? (testResult.message ?? 'Connection test passed.') + (testResult.version ? ` DHIS2 version: ${testResult.version}` : '')
            : testResult.error ?? testResult.message ?? 'Connection test failed.'}
        </Alert>
      )}

      {selectedConfigId && selectedConfig && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight={600}>
            Config options (Phase 4)
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedConfig.review_before_push ?? false}
                onChange={(_, checked) => handleReviewBeforePushChange(checked)}
                disabled={savingReviewOption}
              />
            }
            label="Require review before push (show confirmation before syncing events to DHIS2)"
          />
          {savingReviewOption && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>Saving…</Typography>}
        </Paper>
      )}

      {!selectedConfigId ? (
        <Typography color="text.secondary">Select a config to edit its DHIS2 connection and schema API settings.</Typography>
      ) : loading ? (
        <Typography>Loading…</Typography>
      ) : (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom fontWeight={600}>
            DHIS2 Connection & Schema API
          </Typography>

          {showDhis2Section && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="DHIS2 base URL"
                  placeholder="https://dhis2.example.org"
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Auth type</InputLabel>
                  <Select
                    value={authType}
                    label="Auth type"
                    onChange={(e) => setAuthType(e.target.value)}
                  >
                    <MenuItem value="basic">Username &amp; password (Basic)</MenuItem>
                    <MenuItem value="api_token">API token (Bearer)</MenuItem>
                    <MenuItem value="oauth2">OAuth2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {authType === 'basic' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Username"
                    placeholder="DHIS2 login username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    helperText="Stored; password = API token below (from User Profile → Account)"
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label={authType === 'basic' ? 'Password (API token from profile)' : 'API token'}
                  placeholder="Leave blank to keep existing"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="System info path (for Test connection)"
                  placeholder="/service/api/system/info"
                  value={systemInfoPath}
                  onChange={(e) => setSystemInfoPath(e.target.value)}
                  helperText="Test sends GET to this path (default /api/system/info). If you get 404, set to /service/api/system/info and Save or Test again."
                />
              </Grid>
              {selectedConfig?.schema_source !== 'dhis2_program_data_json' && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Metadata endpoint (path)"
                      placeholder="/api/metadata"
                      value={metadataEndpoint}
                      onChange={(e) => setMetadataEndpoint(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Program-specific URL template"
                      placeholder="{base}/api/programs/{programId}.json?fields=..."
                      value={programSpecificUrlTemplate}
                      onChange={(e) => setProgramSpecificUrlTemplate(e.target.value)}
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label={selectedConfig?.schema_source === 'dhis2_program_data_json' ? 'Program UIDs (comma-separated)' : 'Program IDs (comma-separated)'}
                  placeholder="e.g. wzWPeQVAhBA, eAHvg6zuxvK, b9aWZNpvYQU"
                  value={programIds.join(', ')}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setProgramIds([]);
                      return;
                    }
                    const parts = raw.split(',').map((s) => s.trim());
                    setProgramIds(parts);
                  }}
                  helperText={
                    selectedConfig?.schema_source === 'dhis2_program_data_json'
                      ? 'One or more UIDs. Multiple = one request per UID, schemas merged.'
                      : 'One or more program UIDs. Multiple = fetch each and merge schema.'
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Cache TTL (seconds)"
                  value={cacheTtlSeconds}
                  onChange={(e) => setCacheTtlSeconds(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Timeout (seconds)"
                  value={timeoutSeconds}
                  onChange={(e) => setTimeoutSeconds(e.target.value === '' ? '' : Number(e.target.value))}
                />
              </Grid>
            </Grid>
          )}

          {selectedConfig?.schema_source === 'dhis2_program_data_json' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  ProgramData schema API
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="ProgramData API URL"
                  placeholder="https://api.example.org/schema"
                  value={programdataApiUrl}
                  onChange={(e) => setProgramdataApiUrl(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>HTTP method</InputLabel>
                  <Select
                    value={programdataHttpMethod}
                    label="HTTP method"
                    onChange={(e) => setProgramdataHttpMethod(e.target.value as 'GET' | 'POST')}
                  >
                    <MenuItem value="GET">GET</MenuItem>
                    <MenuItem value="POST">POST</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={programdataAuthSameAsDhis2}
                      onChange={(e) => setProgramdataAuthSameAsDhis2(e.target.checked)}
                    />
                  }
                  label="Use same auth as DHIS2"
                />
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
              Test connection uses GET to base URL + system info path (schema pull uses POST to ProgramData URL).
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Cable />}
                onClick={handleTestConnection}
                disabled={testing || !selectedConfigId}
              >
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
}
